import {ChangeDetectionStrategy, Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {Rechte} from "../../core/model/rechte.model";
import {NachrichtPayload} from "../../core/model/nachricht/nachricht";
import {NachrichtPrepareStepComponent} from "./nachricht-prepare-step/nachricht-prepare-step.component";
import {NachrichtFormComponent} from "../nachricht-form/nachricht-form.component";
import {AnliegenMap} from "../../core/model/anliegen-map";
import {DienststellenMap} from "../../core/model/dienststellen-map";
import {NachrichtPrepareKiwiComponent} from "./nachricht-prepare-kiwi/nachricht-prepare-kiwi.component";
import {NachrichtenTypeEnum} from "../../core/model/nachricht/nachrichten-type.enum";
import {Verfahren} from "../../core/model/verfahren";
import {UserAccountService} from "../../core/services/user-account.service";
import {Subanliegen} from "../../core/model/anliegen";
import {ScrollIntoView} from "../../shared/directives/scroll-into-view.directive";
import {NavigationService} from "../../shared/utils/navigation.service";
import {NachrichtPrepareZavComponent} from "./nachricht-prepare-zav/nachricht-prepare-zav.component";
import {APP_ROUTES} from "../../route-constants";
import {NachrichtStateEnum} from "../nachricht-state.enum";
import {vNiveauEnum} from "../../core/model/nachricht/vniveau.enum";

@Component({
    selector: 'kokos-nachricht-neu',
    imports: [
        NachrichtPrepareStepComponent,
        NachrichtFormComponent,
        NachrichtPrepareKiwiComponent,
        ScrollIntoView,
        NachrichtPrepareZavComponent,
        ScrollIntoView
    ],
    templateUrl: './nachricht-neu.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class NachrichtNeuComponent implements OnInit {

    benutzerRechte: Rechte;
    nachrichtPayload: NachrichtPayload = null;
    nachrichtenVerfahren: Verfahren;
    anliegenMap: AnliegenMap;
    dienststellenMap: DienststellenMap;

    anliegenName: string;
    subanliegenName: string;
    bezug: string;
    anhangErlaubt = false;

    nachrichtState: NachrichtStateEnum = NachrichtStateEnum.PREPARE;

    protected readonly NachrichtenTypeEnum = NachrichtenTypeEnum;
    protected readonly Verfahren = Verfahren;
    protected readonly NachrichtStateEnum = NachrichtStateEnum;

    private readonly route = inject(ActivatedRoute);
    private readonly userAccountService = inject(UserAccountService);
    private readonly nav = inject(NavigationService);

    ngOnInit(): void {
        this.benutzerRechte = this.route.snapshot.data['benutzerRechte'];
        if (this.benutzerRechte) {
            this.nachrichtenVerfahren = this.userAccountService.getVerfahren();
        } else {
            throw new Error('Missing benutzerRechte');
        }
        if (this.userAccountService.isRestricted()) {
            globalThis.setTimeout(() => {
                this.nav.goTo('/' + APP_ROUTES.KEIN_ONLINE_ZUGRIFF);
            }, 0);
        }

        const anliegenUndDienststellen = this.route.snapshot.data['anliegenUndDienststellen'];
        if (!anliegenUndDienststellen) {
            throw new Error('Missing route data: anliegenUndDienststellen');
        }

        this.anliegenMap = anliegenUndDienststellen.anliegenMap;
        this.dienststellenMap = anliegenUndDienststellen.dienststellenMap;
        if (!this.dienststellenMap || Object.keys(this.dienststellenMap).length === 0) {
            throw new Error('Missing Dienststellen');
        }
       this.skipPreparePhase(this.anliegenMap, this.dienststellenMap);
    }

    /**
     * 
     */
    skipPreparePhase(anliegenMap: AnliegenMap, dienststellenMap: DienststellenMap): boolean {
        let nachrichtType: NachrichtenTypeEnum;
        let niveau: vNiveauEnum = vNiveauEnum.NORMAL;
        switch(this.nachrichtenVerfahren) {
            case Verfahren.ZAV:
                nachrichtType = NachrichtenTypeEnum.ZAV;
                break;
            case Verfahren.STEP:
                niveau = vNiveauEnum.DEFAULT;
                if (this.userAccountService.isStepBetrieb()) {
                    nachrichtType = NachrichtenTypeEnum.AA;
                }
                break;
            case Verfahren.KIWI:
                nachrichtType = NachrichtenTypeEnum.FAMKA;
                break;
        }
        if (!nachrichtType) {
            return false;
        }

        // Anliegen
        const anliegen = anliegenMap[nachrichtType];
        if (anliegen?.length !== 1) {
            return false;
        }
        this.setAnliegenName(anliegen[0].name);

        if (anliegen?.[0]?.subanliegen?.length === 1) {
            this.setSubanliegen(anliegen[0].subanliegen[0]);
        } else if (anliegen?.[0]?.subanliegen?.length > 1) {
            return false;
        }

        // Dienststellen - sollte nur eine sein und nicht ausgewählt werden müssen, da keine Step-Person, nur Step-Betrieb
        const dienststellen = dienststellenMap[nachrichtType];
        if (!dienststellen?.dienststellenNummer || !dienststellen?.dienststellenName) {
            return false;
        }
        this.setNachricht({
            empfaengerId: dienststellen.dienststellenNummer,
            empfaengerName: dienststellen.dienststellenName,
            bezug: this.bezug ?? this.anliegenName,
            vNiveau: niveau,
            betreff: '',
            text: '',
        });
        return true;
    }

    setNachricht(nachrichtPayload: NachrichtPayload): void {
        this.nachrichtPayload = nachrichtPayload;
        this.nachrichtState = NachrichtStateEnum.FORM;
    }

    setAnliegenName(anliegenName: string): void {
        this.anliegenName = anliegenName;
    }

    setSubanliegen(subanliegen: Subanliegen): void {
        this.subanliegenName = subanliegen.name;
        this.anhangErlaubt = subanliegen.anhangErlaubt;
        this.bezug = subanliegen.bezug;
    }
}